/* <vsr-reveal> — scroll-triggered entrance animation.
   See docs/js-notes/vsr-reveal.md#note-1. */

(function () {
  'use strict';

  /* Percentage of the viewport height held back at the bottom ed
   See docs/js-notes/vsr-reveal.md#note-2. */
  const BOTTOM_MARGIN = 10;

  class VsrReveal extends HTMLElement {
    connectedCallback() {
      this.targets = Array.from(this.querySelectorAll('.vsr-u-reveal'));
      if (this.targets.length === 0) return;

      // Reduced motion and no IntersectionObserver both return before arming,
      // so nothing is ever hidden on those paths. `is-revealed` is still added:
      // component stylesheets hang their own "has landed" states off it.
      if (window.VSR?.prefersReducedMotion() || !('IntersectionObserver' in window)) {
        this.revealAll();
        return;
      }

      // Measure first. A deferred script runs after the parser finishes, which
      // on a long page is after the first paint — so anything the visitor can
      // already see must be marked revealed before the hide rule is armed,
      // otherwise the hero flashes out and fades back in.
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const line = viewport * (1 - BOTTOM_MARGIN / 100);
      const seen = [];

      this.pending = [];

      this.targets.forEach((target) => {
        if (target.getBoundingClientRect().top < line) seen.push(target);
        else this.pending.push(target);
      });

      if (this.pending.length > 0) {
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            });
          },
          { threshold: 0, rootMargin: `0px 0px -${BOTTOM_MARGIN}% 0px` }
        );

        this.pending.forEach((target) => this.observer.observe(target));

        // One shot. `load` is the moment the page stops growing — fonts and
        // images have settled, so a target that is still short of the line then
        // is short of it because the document ran out, not because the visitor
        // has not scrolled yet. An element upgraded after `load` (the Theme
        // Editor inserting a section) would never see the event, so it sweeps
        // immediately instead; its measurement is already of a settled layout.
        if (document.readyState === 'complete') {
          this.sweep();
        } else {
          this.controller = new AbortController();
          window.addEventListener('load', () => this.sweep(), {
            once: true,
            signal: this.controller.signal
          });
        }
      }

      seen.forEach((target) => target.classList.add('is-revealed'));

      // Last statement in the method, deliberately. Everything that can throw
      // has already run, and every target that is not visible now has an
      // observer that will un-hide it.
      this.classList.add('is-armed');
    }

    /* Reveal every target the document can never scroll high enoug
   See docs/js-notes/vsr-reveal.md#note-3. */
    sweep() {
      if (!this.observer) return;

      const doc = document.documentElement;
      const viewport = window.innerHeight || doc.clientHeight;
      const line = viewport * (1 - BOTTOM_MARGIN / 100);
      const remaining = Math.max(0, doc.scrollHeight - doc.clientHeight - window.scrollY);

      this.pending = this.pending.filter((target) => {
        if (target.classList.contains('is-revealed')) return false;
        if (target.getBoundingClientRect().top - remaining < line) return true;

        target.classList.add('is-revealed');
        this.observer.unobserve(target);
        return false;
      });

      if (this.pending.length === 0) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.pending = [];

      // Drops the pending `load` listener with the element. Without this a
      // section removed in the Theme Editor keeps a closure over it alive, and
      // reconnecting would add a second one.
      this.controller?.abort();
      this.controller = null;

      // An element sitting in the document with no live observer must not be
      // able to hold its content at opacity 0. The Theme Editor moves sections
      // by disconnecting and reconnecting them, and connectedCallback
      // re-measures and re-arms in the same task, so this costs no flicker.
      this.classList.remove('is-armed');
    }

    revealAll() {
      this.targets.forEach((target) => target.classList.add('is-revealed'));
    }
  }

  if (window.VSR) window.VSR.define('vsr-reveal', VsrReveal);
  else if (!customElements.get('vsr-reveal')) customElements.define('vsr-reveal', VsrReveal);
})();
